import { Controller } from "@hotwired/stimulus"

/**
 * Info Modal Controller
 *
 * A generic controller for displaying informational modals.
 * Used for ranking type explanations and other help text.
 *
 * @example HTML structure
 *   <div data-controller="info-modal">
 *     <button data-action="info-modal#open" data-info-modal-target="trigger">
 *       <svg><!-- info icon --></svg>
 *     </button>
 *     <div data-info-modal-target="modal" class="hidden">
 *       <!-- modal content -->
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["trigger", "modal"]

  /**
   * Opens the info modal.
   * Adds event listener for Escape key to close.
   *
   * @param {Event} event - The click event
   */
  open(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.hasModalTarget) return

    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")

    // Store bound handler for cleanup
    this.boundKeyHandler = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeyHandler)

    // Focus the modal
    this.modalTarget.focus()
  }

  /**
   * Closes the info modal.
   * Removes event listeners and restores body scroll.
   */
  close() {
    if (!this.hasModalTarget) return

    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")

    // Clean up event listener
    if (this.boundKeyHandler) {
      document.removeEventListener("keydown", this.boundKeyHandler)
      this.boundKeyHandler = null
    }

    // Return focus to trigger
    if (this.hasTriggerTarget) {
      this.triggerTarget.focus()
    }
  }

  /**
   * Handles click on backdrop to close modal.
   *
   * @param {Event} event - The click event
   */
  closeOnBackdrop(event) {
    // Only close if clicking directly on the backdrop (not the modal content)
    if (event.target === event.currentTarget) {
      this.close()
    }
  }

  /**
   * Handles keydown events when modal is open.
   * Closes modal on Escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  /**
   * Stops click event propagation.
   * Used to prevent clicks on modal content from closing the modal.
   *
   * @param {Event} event - The click event
   */
  stopPropagation(event) {
    event.stopPropagation()
  }

  /**
   * Cleanup when controller disconnects.
   * Ensures no memory leaks from event listeners.
   */
  disconnect() {
    if (this.boundKeyHandler) {
      document.removeEventListener("keydown", this.boundKeyHandler)
    }
    document.body.classList.remove("overflow-hidden")
  }
}
